import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@examdojo/core/environment';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { QuestionStoreModel } from '@examdojo/question';
import { ExamdojoSupabaseService, SupabaseBaseHttpService } from '@examdojo/supabase';
import { from, map, Observable } from 'rxjs';
import {
  REFERENCE_QUESTION_STORAGE_BUCKET,
  ReferenceQuestionHttpModel,
  ReferenceQuestionViewHttpModel,
} from './reference-question.model';

@Injectable({ providedIn: 'root' })
export class ReferenceQuestionHttpService extends SupabaseBaseHttpService<'reference_questions'> {
  constructor(
    private readonly http: HttpClient,
    protected override readonly supabase: ExamdojoSupabaseService,
  ) {
    super(supabase);
  }

  readonly schema = 'public';
  readonly tableName = 'reference_questions';
  override readonly storage_bucket = REFERENCE_QUESTION_STORAGE_BUCKET;

  fetchPaginatedListView(
    selectQueryConfig: SupabaseSelectQueryConfig = {},
  ): Observable<{ data: ReferenceQuestionViewHttpModel[]; totalCount: number }> {
    const query = this.supabase.createSelectQuery('public', 'reference_question_items_view', selectQueryConfig);

    return from(query).pipe(
      map(({ data, count }) => ({
        data: data!,
        totalCount: count!,
      })),
    ) as Observable<{ data: ReferenceQuestionViewHttpModel[]; totalCount: number }>;
  }

  fetchByQuestionId(questionId: QuestionStoreModel['id']): Observable<ReferenceQuestionHttpModel | null> {
    return from(
      this.supabase.client
        .rpc('get_reference_question_by_question_id', { question_id: questionId })
        .single()
        .throwOnError()
        .then((response) => response.data),
    );
  }

  fetchSyllabusIdByReferenceQuestionId(
    referenceQuestionId: ReferenceQuestionHttpModel['id'],
  ): Observable<number | null> {
    return from(
      this.supabase.client
        .rpc('get_syllabus_id_for_reference_question', { reference_question_id: referenceQuestionId })
        .throwOnError()
        .then((response) => response.data),
    );
  }

  fetchReferenceQuestionStatus(id: ReferenceQuestionHttpModel['id']): Observable<string | null> {
    return from(
      this.supabase.client
        .from('reference_questions')
        .select('status')
        .eq('id', id)
        .single()
        .throwOnError()
        .then((response) => response.data?.status as string | null),
    );
  }

  fetchMarkschemeImages(id: ReferenceQuestionHttpModel['id']) {
    return from(
      this.supabase.client
        .from('reference_question_markscheme_images')
        .select('*')
        .eq('reference_question', id)
        .throwOnError(),
    );
  }

  addToDataset(referenceQuestionId: ReferenceQuestionHttpModel['id'], datasetName: string) {
    const url = `${environment.examdojo.cmsApiUrl}/reference-questions/${referenceQuestionId}/datasets/${datasetName}/rows`;
    return this.http.post(url, {});
  }

  fetchEditHistory(referenceQuestionId: ReferenceQuestionHttpModel['id']) {
    return from(
      this.supabase.client
        .from('reference_questions_history')
        .select('*')
        .eq('id', referenceQuestionId)
        .order('sys_period', { ascending: false })
        .throwOnError(),
    ).pipe(map((response) => response.data || []));
  }

  fetchStatusSnapshots(referenceQuestionId: ReferenceQuestionHttpModel['id']) {
    return from(
      this.supabase.client
        .from('reference_question_snapshots')
        .select('*')
        .eq('id', referenceQuestionId)
        .order('snapshot_id', { ascending: false })
        .throwOnError(),
    ).pipe(map((response) => response.data));
  }

  createSeedQuestion(referenceQuestionId: number): Observable<number | null> {
    return from(
      this.supabase.client
        .rpc('create_derivative_seed_question_from_reference_question', {
          reference_question_id: referenceQuestionId,
        })
        .throwOnError()
        .then((response) => response.data),
    );
  }
}
